import { color, create, percent, useTheme } from '@amcharts/amcharts4/core';
import { DateAxis, LineSeries, ValueAxis, XYChart, XYChartScrollbar, XYCursor } from '@amcharts/amcharts4/charts';
// eslint-disable-next-line import/extensions
import am4themes_animated from '@amcharts/amcharts4/themes/animated.js';
import { autoinject, bindable } from 'aurelia-framework';
import { CustomerSettings } from '../../classes/customer-settings';
import { PriceHistoryClient } from '@dynamik/common/clients/reporting-clients';
import { debounceTime, Subject, switchMap, takeUntil, tap } from '../../rxjs-local';
import { ThemeService } from '../../services/theme-service';

@autoinject
export class PriceHistoryByDate {
  constructor(private customerSettingsClass: CustomerSettings, private priceHistoryClient: PriceHistoryClient, private themeService: ThemeService) { }

  chart: HTMLElement;
  priceChartObject: XYChart;
  loading: boolean;
  error: Error;

  @bindable
  securityId: number;

  @bindable
  availableInstrumentId: string;

  destroy$ = new Subject<void>();

  subscribeToFilter() {
    this.customerSettingsClass.filter$
      .pipe(
        debounceTime(500),
        tap(() => {
          this.error = undefined;
          this.loading = true;
        }),
        switchMap(async filter => this.getData(filter.to).catch(e => e as Error)),
        takeUntil(this.destroy$)
      )
      .subscribe(x => {
        this.loading = false;
        if (x instanceof Error) {
          this.error = x;
        } else {
          this.priceChartObject.data = x;
        }
      });
  }

  async attached() {
    useTheme(am4themes_animated);
    this.priceChartObject?.dispose();
    this.priceChartObject = create(this.chart, XYChart);

    this.priceChartObject.width = percent(100);
    this.priceChartObject.height = percent(100);
    const dateAxis = this.priceChartObject.xAxes.push(new DateAxis());
    dateAxis.renderer.minGridDistance = 50;
    const labelCoolour = this.themeService.getChartLabelColour(undefined);
    dateAxis.renderer.labels.template.fill = color(labelCoolour);
    dateAxis.renderer.grid.template.stroke = color(labelCoolour);

    const valueAxis = this.priceChartObject.yAxes.push(new ValueAxis());
    valueAxis.renderer.labels.template.fill = color(labelCoolour);
    valueAxis.renderer.grid.template.stroke = color(labelCoolour);
    // Create series
    const lineSeries = this.priceChartObject.series.push(new LineSeries());
    lineSeries.dataFields.valueY = 'imported';
    lineSeries.dataFields.dateX = 'date';
    lineSeries.strokeWidth = 2;
    lineSeries.minBulletDistance = 10;
    lineSeries.tooltipText = '{valueY}';
    lineSeries.tooltip.pointerOrientation = 'vertical';
    lineSeries.tooltip.background.cornerRadius = 20;
    lineSeries.tooltip.background.fillOpacity = 0.5;
    lineSeries.tooltip.label.padding(12, 12, 12, 12);
    // create Series 2
    // Create series
    const lineSeries2 = this.priceChartObject.series.push(new LineSeries());
    lineSeries2.dataFields.valueY = 'manual';
    lineSeries2.dataFields.dateX = 'date';
    lineSeries2.strokeWidth = 2;
    lineSeries2.minBulletDistance = 10;
    lineSeries2.tooltipText = '{valueY}';
    lineSeries2.tooltip.pointerOrientation = 'vertical';
    lineSeries2.tooltip.background.cornerRadius = 20;
    lineSeries2.tooltip.background.fillOpacity = 0.5;
    lineSeries2.tooltip.label.padding(12, 12, 12, 12);
    // // Add scrollbar
    const scrollbar = this.priceChartObject.scrollbarX = new XYChartScrollbar();
    scrollbar.series.push(lineSeries);
    scrollbar.background.fillOpacity = 0.1;
    // //
    // // // Add cursor
    const cursor = this.priceChartObject.cursor = new XYCursor();
    cursor.xAxis = dateAxis;
    cursor.snapToSeries = lineSeries;
    cursor.lineX.stroke = color(labelCoolour);
    cursor.lineY.stroke = color(labelCoolour);
    this.subscribeToFilter();
  }

  async detached() {
    this.destroy$.next();
    this.destroy$.complete();
    this.priceChartObject.dispose();
  }

  async getData(date: string) {
    if (this.securityId) {
      return this.priceHistoryClient.portfolio(this.customerSettingsClass.selectedCustomer.identityId, this.securityId, date);
    } else if (this.availableInstrumentId) {
      return this.priceHistoryClient.marketIndex(this.availableInstrumentId, date, this.customerSettingsClass.selectedCustomer.identityId.toString());
    }
  }
}
